import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../global/supabase';
import { BRANDING, CLIENT_ID, LOCATIONS, MEDICATIONS } from '../../config';

const DEPLOYMENT_ID = CLIENT_ID;

const DEFAULTS = {
  daily: {
    system_role:
      "You are an AI compliance analyst for Fire Department of Bellevue-Dayton's controlled substance inventory management system (NarcoticTrack). Your role is to analyze the last 24 hours of narcotic inventory logs across all units and generate a concise, actionable compliance report. You focus on identifying discrepancies, missing logs, policy violations, and patterns that require supervisor attention. You are direct, precise, and prioritize patient safety and DEA compliance.",
    flag_criteria:
      'CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Patient Use/Restock or Expiration/Restock), missing/null required fields, negative quantities\nWARNING: Tag number changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification)\nINFO: Notable personnel notes, run numbers logged, routine tag verifications, new personnel',
    analysis_priorities: '',
    additional_instructions: '',
  },
  weekly: {
    system_role:
      'You are an AI compliance strategist for Fire Department of Bellevue-Dayton, generating a WEEKLY intelligence report for narcotic inventory and controlled substance management. Think like a Division Chief reviewing a week of operations. Focus on PATTERNS and TRENDS across the entire week, not individual daily incidents. Identify systemic issues, personnel patterns, compliance trajectory, and strategic recommendations. Your audience is senior leadership.',
    flag_criteria:
      'CRITICAL: Any unit with zero logs in 24h when active, medication quantity discrepancy without documented reason (such as Patient Use/Restock or Expiration/Restock), missing/null required fields, negative quantities\nWARNING: Tag number changes without documentation, units with only 1 log in 24h, quantities reaching zero, same personnel logging all entries (no cross-verification)\nINFO: Notable personnel notes, run numbers logged, routine tag verifications, new personnel',
    analysis_priorities:
      'Compliance trends (improving/stable/declining)\nMedication usage patterns (unusual consumption, zero-quantity events, restock anomalies)\nPersonnel accountability (coverage gaps, cross-verification)\nSystemic issues (process/training gaps)\nDaily flags follow-up (were critical flags addressed?)',
    additional_instructions: '',
  },
};

function PromptTemplateEditor({ userInfo }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [form, setForm] = useState({ ...DEFAULTS.daily });
  const [savedForm, setSavedForm] = useState({ ...DEFAULTS.daily });
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const hasChanges =
    form.system_role !== savedForm.system_role ||
    form.flag_criteria !== savedForm.flag_criteria ||
    form.analysis_priorities !== savedForm.analysis_priorities ||
    form.additional_instructions !== savedForm.additional_instructions;

  const loadTemplate = useCallback(
    async (type) => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('ai_prompt_templates')
          .select('*')
          .eq('prompt_type', type)
          .eq('deployment_id', DEPLOYMENT_ID)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
          throw fetchError;
        }

        if (data) {
          const loaded = {
            system_role: data.system_role || '',
            flag_criteria: data.flag_criteria || '',
            analysis_priorities: data.analysis_priorities || '',
            additional_instructions: data.additional_instructions || '',
          };
          setForm(loaded);
          setSavedForm(loaded);
          setTemplateId(data.id);
        } else {
          const defaults = DEFAULTS[type];
          setForm({ ...defaults });
          setSavedForm({ ...defaults });
          setTemplateId(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load template');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadTemplate(activeTab);
  }, [activeTab, loadTemplate]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (templateId) {
        const { error: updateError } = await supabase
          .from('ai_prompt_templates')
          .update({
            system_role: form.system_role,
            flag_criteria: form.flag_criteria,
            analysis_priorities: form.analysis_priorities || null,
            additional_instructions: form.additional_instructions || '',
            updated_by: userInfo?.id || null,
          })
          .eq('id', templateId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('ai_prompt_templates')
          .insert({
            prompt_type: activeTab,
            name: 'Default',
            system_role: form.system_role,
            flag_criteria: form.flag_criteria,
            analysis_priorities: form.analysis_priorities || null,
            additional_instructions: form.additional_instructions || '',
            is_active: true,
            deployment_id: DEPLOYMENT_ID,
            created_by: userInfo?.id || null,
            updated_by: userInfo?.id || null,
          });

        if (insertError) throw insertError;
      }

      setSavedForm({ ...form });
      setSuccess('Template saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = DEFAULTS[activeTab];
    setForm({ ...defaults });
  };

  const handleDiscard = () => {
    setForm({ ...savedForm });
  };

  const textareaStyle = {
    width: '100%',
    minHeight: '120px',
    padding: '10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    resize: 'vertical',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontWeight: '600',
    fontSize: '14px',
    color: '#1a202c',
    marginBottom: '6px',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px' }}>
        {['daily', 'weekly'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '12px',
              background:
                activeTab === tab ? BRANDING.primaryColor : '#e2e8f0',
              color: activeTab === tab ? '#fff' : '#555',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius:
                tab === 'daily' ? '8px 0 0 8px' : '0 8px 8px 0',
            }}
          >
            {tab === 'daily' ? 'Daily Report' : 'Weekly Report'}
          </button>
        ))}
        <button
          onClick={() => setShowGuide(!showGuide)}
          style={{
            marginLeft: '12px',
            padding: '8px 14px',
            background: showGuide ? BRANDING.primaryColor : 'transparent',
            color: showGuide ? '#fff' : BRANDING.primaryColor,
            border: `2px solid ${BRANDING.primaryColor}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
          }}
        >
          {showGuide ? 'Hide Guide' : 'Guide'}
        </button>
      </div>

      {/* Unsaved Changes Indicator */}
      {hasChanges && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '6px',
            padding: '8px 14px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#92400e',
            fontWeight: '600',
          }}
        >
          You have unsaved changes
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div
          style={{
            background: '#f0fff4',
            border: '1px solid #9ae6b4',
            borderRadius: '6px',
            padding: '8px 14px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#22543d',
          }}
        >
          {success}
        </div>
      )}
      {error && (
        <div
          style={{
            background: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: '6px',
            padding: '8px 14px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#c53030',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Main Editor */}
        <div style={{ flex: 1 }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#718096' }}>
              Loading template...
            </p>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>System Role</label>
                <textarea
                  style={textareaStyle}
                  value={form.system_role}
                  onChange={(e) =>
                    setForm({ ...form, system_role: e.target.value })
                  }
                  placeholder="AI persona and instructions..."
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Flag Criteria</label>
                <textarea
                  style={textareaStyle}
                  value={form.flag_criteria}
                  onChange={(e) =>
                    setForm({ ...form, flag_criteria: e.target.value })
                  }
                  placeholder="Severity classification rules..."
                />
              </div>

              {activeTab === 'weekly' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Analysis Priorities</label>
                  <textarea
                    style={textareaStyle}
                    value={form.analysis_priorities}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        analysis_priorities: e.target.value,
                      })
                    }
                    placeholder="Weekly analysis focus areas..."
                  />
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Additional Instructions</label>
                <textarea
                  style={{ ...textareaStyle, minHeight: '80px' }}
                  value={form.additional_instructions}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      additional_instructions: e.target.value,
                    })
                  }
                  placeholder="Deployment-specific customizations..."
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  style={{
                    padding: '10px 20px',
                    background:
                      saving || !hasChanges ? '#a0aec0' : BRANDING.primaryColor,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor:
                      saving || !hasChanges ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                  }}
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button
                  onClick={handleReset}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: '#d32f2f',
                    border: '2px solid #d32f2f',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                  }}
                >
                  Reset to Defaults
                </button>
                {hasChanges && (
                  <button
                    onClick={handleDiscard}
                    style={{
                      padding: '10px 20px',
                      background: 'transparent',
                      color: '#718096',
                      border: '2px solid #718096',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                    }}
                  >
                    Discard Changes
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Side Panel Guide */}
        {showGuide && (
          <div
            style={{
              width: '260px',
              flexShrink: 0,
              background: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              fontSize: '12px',
              lineHeight: '1.6',
              alignSelf: 'flex-start',
            }}
          >
            <h4 style={{ margin: '0 0 12px', color: BRANDING.primaryColor }}>
              Reference Guide
            </h4>

            <div style={{ marginBottom: '12px' }}>
              <strong>Units</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                {LOCATIONS.map((loc) => (
                  <li key={loc.id}>
                    {loc.name}{' '}
                    <span style={{ color: '#718096' }}>({loc.type})</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong>Medications</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                {MEDICATIONS.map((med) => (
                  <li key={med.id}>
                    {med.displayName}{' '}
                    <span style={{ color: '#718096' }}>
                      (Schedule {med.schedule})
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <strong>Severity Levels</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                <li>
                  <span style={{ color: '#d32f2f', fontWeight: '600' }}>
                    critical
                  </span>
                </li>
                <li>
                  <span style={{ color: '#f57c00', fontWeight: '600' }}>
                    warning
                  </span>
                </li>
                <li>
                  <span style={{ color: '#1976d2', fontWeight: '600' }}>
                    info
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <strong>Status Values</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                <li>
                  <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                    compliant
                  </span>
                </li>
                <li>
                  <span style={{ color: '#f57c00', fontWeight: '600' }}>
                    needs_attention
                  </span>
                </li>
                <li>
                  <span style={{ color: '#d32f2f', fontWeight: '600' }}>
                    non_compliant
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptTemplateEditor;
